import { Component, computed, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { ResetPasswordDataService } from 'src/app/services/reset-password-data.service';
import { ErrorSnackbarComponent } from '../../custom/error-snackbar/error-snackbar.component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ResetPasswordService } from 'src/app/services/reset-password.service';
import { SuccessSnackbarComponent } from '../../custom/success-snackbar/success-snackbar.component';
import { AuthService } from 'src/app/services/auth.service';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { LogEventsEnum } from 'src/app/firebase.model';
import { NgOptimizedImage } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatRippleModule, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-new-password',
  templateUrl: './new-password.component.html',
  styleUrls: ['./new-password.component.css'],
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgOptimizedImage,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatRadioModule,
    MatDialogModule,
    MatSnackBarModule,
    MatRippleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSnackBarModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatDividerModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatBottomSheetModule,
    MatMenuModule,
    MatBadgeModule,
  ],
})
export class NewPasswordComponent {
  private analytics = inject(Analytics);
  newPasswordForm: FormGroup;
  valueChangeSub: Subscription;
  isLightMode = computed(() => this.themeService.isLightMode());
  hideNewPassword = true;
  hideConfirmPassword = true;

  constructor(
    private router: Router,
    private resetPasswordDataService: ResetPasswordDataService,
    private snackBar: MatSnackBar,
    private resetPasswordService: ResetPasswordService,
    private authService: AuthService,
    private themeService: ThemeService
  ) {
    this.newPasswordForm = new FormGroup({
      newPassword: new FormControl('', [
        Validators.required,
        Validators.pattern(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$!%*?&])[A-Za-z\d@#$!%*?&]{8,}$/
        ),
      ]),
      confirmPassword: new FormControl('', [
        // Validators.required,
        this.confirmPasswordValidator(),
      ]),
    });
    this.valueChangeSub = new Subscription();
  }

  ngOnInit() {
    logEvent(this.analytics, LogEventsEnum.auth_forgot_new_password);
    this.valueChangeSub.add(
      this.newPasswordForm.get('newPassword')?.valueChanges.subscribe(() => {
        this.newPasswordForm.get('confirmPassword')?.updateValueAndValidity();
      })
    );
  }

  confirmPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.newPasswordForm) return null; // Guard clause if form is not initialized

      const newPassword = this.newPasswordForm.get('newPassword')?.value;
      const confirmPassword = control.value;

      return newPassword === confirmPassword ? null : { mismatch: true };
    };
  }

  onSubmit() {
    if (this.newPasswordForm.invalid) {
      this.newPasswordForm.markAllAsTouched();
      return;
    }

    const formValues = this.newPasswordForm.value;

    const email = this.resetPasswordDataService.getEmail();
    const vcode = this.resetPasswordDataService.getVcode();
    if (!email || !vcode) {
      this.router.navigate(['/forgot-password']);
      this.snackBar.openFromComponent(ErrorSnackbarComponent, {
        data: {
          message:
            'Email and confirmation code not found. Please reenter email',
        },
        duration: 3000,
      });
      return;
    }

    this.resetPasswordService
      .mutate({
        loginInput: {
          username: email,
          vcode: vcode,
          password: formValues.newPassword,
        },
      })
      .subscribe({
        next: () => {
          this.router.navigate(['/login'], { replaceUrl: true }).then(() => {
            window.history.replaceState(null, '', window.location.href);
          });
          this.authService.logout();
          this.snackBar.openFromComponent(SuccessSnackbarComponent, {
            data: {
              message: 'Password has been successfully reset',
            },
            duration: 3000,
          });
        },
        error: (error) => {
          console.error('Error logging in:', error.message);
          this.router.navigate(['/forgot-password']);
          this.snackBar.openFromComponent(ErrorSnackbarComponent, {
            data: {
              message: 'Reseting password failed. Please reenter email',
            },
            duration: 3000,
          });
        },
      });
  }

  toggleNewPasswordVisibility() {
    this.hideNewPassword = !this.hideNewPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.hideConfirmPassword = !this.hideConfirmPassword;
  }
}
